import { cn, Kbd, KbdGroup } from "@nook/ui"
import { useEffect, useRef, type ReactNode } from "react"
import { tinykeys } from "tinykeys"

export const isMac = /Mac|iPhone|iPad/.test(navigator.platform)

const LABELS: Record<string, string> = {
  $mod: isMac ? "⌘" : "Ctrl",
  Shift: isMac ? "⇧" : "Shift",
  Control: isMac ? "⌃" : "Ctrl",
  Alt: isMac ? "⌥" : "Alt",
  Enter: "↵",
  Backspace: "⌫",
  Escape: "esc",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  "=": "+",
  "-": "−",
}

/**
 * Key caps for a tinykeys binding: `"$mod+k"` → ⌘ K, `"g l"` → G L. `[Shift]+?`
 * shows as `?`, and a bare `–` between keys is a range, not a cap.
 */
export function Keys({ keys, className }: { keys: string; className?: string }) {
  return (
    <KbdGroup className={cn("shrink-0 max-md:hidden", className)}>
      {keys.split(/ |(?<=\w|\])\+/).filter((key) => key !== "[Shift]").map((key, index) =>
        key === "–" ? (
          <span key={index}>–</span>
        ) : (
          <Kbd key={index}>{LABELS[key] ?? key.toUpperCase()}</Kbd>
        ),
      )}
    </KbdGroup>
  )
}

/** A footer's `J K Move`. */
export function Hint({ keys, children }: { keys: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1.5 max-md:hidden">
      <Keys keys={keys} />
      {children}
    </span>
  )
}

/**
 * Only `$mod` chords reach through a text field and past an open dialog; the
 * rest, `$mod+Backspace` (a word delete while typing) among them, wait until the
 * user is not typing, and until any menu or dialog they did not start in has closed.
 */
function blocked(event: KeyboardEvent) {
  const target = event.target as Element
  if (target.closest("input, textarea, select, [contenteditable=true]")) return true
  // A focused link or button keeps its own Enter.
  if (event.key === "Enter" && target.closest("a, button")) return true
  // Menus, popovers and confirms own their keys; no binding is registered inside one.
  if (target.closest("[role=alertdialog], [data-radix-popper-content-wrapper]")) return true
  const overlay = document.querySelector("[role=dialog][data-state=open], [role=alertdialog][data-state=open], [role=menu]")
  return overlay !== null && !overlay.contains(target)
}

/** Window keybindings for as long as the caller is mounted (and `enabled`). */
export function useKeys(map: Record<string, (event: KeyboardEvent) => void>, enabled = true) {
  const latest = useRef(map)
  latest.current = map
  const bindings = Object.keys(map).join("\n")

  useEffect(() => {
    if (!enabled) return
    return tinykeys(
      window,
      Object.fromEntries(
        bindings.split("\n").map((binding) => [
          binding,
          (event: KeyboardEvent) => {
            if (!/\$mod\+(?!Backspace)/.test(binding) && blocked(event)) return
            event.preventDefault()
            latest.current[binding]?.(event)
          },
        ]),
      ),
      { ignore: (event) => event.isComposing },
    )
  }, [bindings, enabled])
}
